import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X } from 'lucide-react'; 
import './Article.css';

import { URLS } from '../config';
import { components } from '../components';
import Logo from '../assets/logo.png'; 

const Article = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [articles, setArticles] = useState<any[]>([]);
    const [currentIndex, setCurrentIndex] = useState<number>(-1);

    useEffect(() => {
        fetch(URLS.GET_DATA)
            .then((res) => res.json())
            .then((data) => {
                const items = data.issues || [];
                const index = items.findIndex((item: any) => item.id === Number(id));
                setArticles(items);
                setCurrentIndex(index);
            });
    }, [id]);

    // Use useCallback for navigation handlers based on component state
    const handleBack = useCallback(() => {
        // FIX: Navigate directly to the /order pages
        navigate('/Blog'); 
    }, [navigate]);

    const handlePrev = useCallback(() => {
        if (currentIndex > 0) {
            navigate(`/article/${articles[currentIndex - 1].id}`);
        }
    }, [currentIndex, articles, navigate]);

    const handleNext = useCallback(() => {
        if (currentIndex < articles.length - 1) {
            navigate(`/article/${articles[currentIndex + 1].id}`);
        }
    }, [currentIndex, articles, navigate]);

    // Keyboard Navigation Effect
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'ArrowLeft') handlePrev();
            if (e.key === 'ArrowRight') handleNext();
            // Optional: Add Escape key to go back
            if (e.key === 'Escape') handleBack(); 
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handlePrev, handleNext, handleBack]); // Depend on memoized handlers

    const article = articles[currentIndex];

    if (!article) {
        return (
            <div className="not-found">
                ❌ Article not found
            </div>
        );
    }
    
    // NOTE: renderBottomTabBar is not needed if the component is rendered directly in JSX

    return (
        <>
            {/* Main wrapper, adjusted for BottomTabBar height */}
            <div style={{ paddingBottom: '60px' }}> 
                
                {/* Custom Header with Close Button */}
             <div className="article-header" style={{ position: 'sticky', top: 0, zIndex: 10 }}>
    <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between',  // Logo left, X button right
        alignItems: 'center',
        padding: '30px 15px 10px 20px', 
        backgroundColor: '#fff',
        borderBottom: '1px solid #eee',
        height: '82px'  // Fixed height for header  
    }}>
        {/* Logo on the left */}
       <img 
            src={Logo}       // <-- use imported Logo
            alt="Logo" 
                        onClick={() => navigate('/')} // Navigate to home on click
            style={{ height: '45px', objectFit: 'contain' }}
        />

        {/* Close/Exit Button on the right */}
        <button 
            onClick={handleBack} 
            style={{ 
                background: 'none', 
                border: 'none', 
                cursor: 'pointer', 
                padding: '5px' 
            }}
            aria-label="Close Article"
        >
           BACK
        </button>
    </div>
</div>

                
                <div className="main-container">
                    <main className="article-content-wrapper">
                        
                        {/* Navigation Arrows */}
                        <div className="article-nav">
                            <button 
                                onClick={handlePrev} 
                                disabled={currentIndex === 0} 
                                className="arrow-btn"
                                aria-label="Previous Article"
                            >
                                <ChevronLeft size={28} />
                            </button>
                            <button 
                                onClick={handleNext} 
                                disabled={currentIndex === articles.length - 1} 
                                className="arrow-btn"
                                aria-label="Next Article"
                            >
                                <ChevronRight size={28} />
                            </button>
                        </div>

                        {/* Article Title */}
                        <h1 className="article-title">{article.title}</h1>

                        {/* Metadata */}
                        <div className="article-meta">
                            <p>
                                <span className="author">By {article.author}</span> |{' '}
                                <span>{article.category || 'General'}</span> |{' '}
                                <span>{article.date || 'Unknown Date'}</span>
                            </p>
                        </div>

                        {/* Image */}
                        <div className="article-image">
                            <img
                                src={article.image}
                                alt={article.title}
                                onError={(e) => {
                                    (e.target as HTMLImageElement).src = '/fallback.jpg';
                                }}
                            />
                        </div>

                        {/* Description */}
                        <p className="article-description">
                            {article.description}
                        </p>
                    </main>
                </div>
                
                {/* Bottom Tab Bar */}
                <components.BottomTabBar />
            </div>
        </>
    );
};

export default Article;